/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CARD_HOVER, HERO_ENTER, HERO_ENTER_DELAYED } from '@/lib/motionPresets';
import { Stethoscope, UserRound, ArrowRight, ShieldCheck, Sparkles, Globe, Zap } from 'lucide-react';
import { AVAILABLE_LANGUAGES } from '@/i18n';
import Logo from '@/components/Logo';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const LangSwitcher = () => {
  const { i18n } = useTranslation();
  const change = (code) => { i18n.changeLanguage(code); localStorage.setItem('sc_lang', code); };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="text-[11px] px-3 py-1.5 rounded-full uppercase font-semibold glass hover:bg-card flex items-center gap-1.5 shadow-elev-sm transition-colors" data-testid="welcome-lang-trigger">
          <Globe className="h-3 w-3" /> {i18n.language.toUpperCase()}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[60vh] overflow-y-auto">
        {AVAILABLE_LANGUAGES.map(l => (
          <DropdownMenuItem key={l.code} onClick={() => change(l.code)} className={i18n.language === l.code ? 'font-semibold text-primary' : ''}>
            <span className="mr-2 text-base">{l.flag}</span>{l.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const RoleCard = ({ to, icon: Icon, title, subtitle, bullets, cta, testId, variant = 'primary', navigate }) => {
  const isPrimary = variant === 'primary';
  return (
    <motion.button
      {...CARD_HOVER}
      onClick={() => navigate(to)}
      className="group text-left w-full rounded-2xl bg-card border border-border/60 p-6 md:p-7 shadow-elev-md hover:shadow-elev-lg transition-shadow flex flex-col gap-4 relative overflow-hidden"
      data-testid={testId}
    >
      <div className={`absolute -top-16 -right-16 h-40 w-40 rounded-full blur-3xl opacity-30 ${isPrimary ? 'bg-primary' : 'bg-accent'}`} />
      <div className={`relative w-14 h-14 rounded-2xl flex items-center justify-center ${isPrimary ? 'bg-primary/10 text-primary' : 'bg-accent/10 text-accent'}`}>
        <Icon className="h-7 w-7" />
      </div>
      <div className="relative">
        <h2 className="font-display font-semibold text-xl md:text-2xl tracking-tight">{title}</h2>
        <p className="text-sm text-muted-foreground mt-1.5">{subtitle}</p>
      </div>
      <ul className="relative space-y-2 mt-1">
        {bullets.map((b) => (
          <li key={b.id} className="flex items-start gap-2.5 text-sm">
            <span className={`mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0 ${isPrimary ? 'bg-primary' : 'bg-accent'}`} />
            <span className="text-foreground/80">{b.text}</span>
          </li>
        ))}
      </ul>
      <div className={`relative mt-2 inline-flex items-center gap-2 font-semibold text-sm ${isPrimary ? 'text-primary' : 'text-accent'} group-hover:gap-3 transition-all`}>
        {cta} <ArrowRight className="h-4 w-4" />
      </div>
    </motion.button>
  );
};

const useRoleBullets = (t) => useMemo(() => ({
  professional: [1, 2, 3, 4].map((i) => ({ id: `pro-b${i}`, text: t(`welcomeProBullet${i}`) })),
  patient: [1, 2, 3, 4].map((i) => ({ id: `pat-b${i}`, text: t(`welcomePatBullet${i}`) })),
}), [t]);

const WelcomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const bullets = useRoleBullets(t);
  return (
    <div className="min-h-screen bg-background bg-hero-gradient relative overflow-hidden" data-testid="welcome-page">
      {/* Decorative background grid */}
      <div className="absolute inset-0 bg-grain opacity-40 pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-4 py-6 md:py-12">
        <div className="flex items-center justify-between mb-8 md:mb-12">
          <Logo size={52} withText textClassName="text-lg" tagline={t('tagline')} />
          <LangSwitcher />
        </div>

        <motion.div {...HERO_ENTER} className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full glass text-[10px] sm:text-[11px] font-semibold uppercase tracking-normal text-primary mb-4 max-w-full" data-testid="welcome-badge">
            <Zap className="h-3 w-3 flex-shrink-0" aria-hidden="true" /> <span className="truncate">{t('welcomeBadge')}</span>
          </div>
          <h1 className="font-display font-semibold text-[1.75rem] sm:text-3xl md:text-5xl tracking-tight max-w-3xl mx-auto leading-[1.12] text-balance px-1" data-testid="welcome-headline">
            {t('welcomeChooseTitle')}
          </h1>
          <p className="mt-4 text-sm sm:text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed text-pretty px-1" data-testid="welcome-subtitle">
            {t('welcomeChooseSubtitle')}
          </p>
        </motion.div>

        <motion.div {...HERO_ENTER_DELAYED} className="grid gap-5 md:grid-cols-2 max-w-4xl mx-auto">
          <RoleCard
            key="role-professional"
            navigate={navigate}
            to="/register?role=professional"
            icon={Stethoscope}
            title={t('welcomeProfessionalTitle')}
            subtitle={t('welcomeProfessionalSubtitle')}
            bullets={bullets.professional}
            cta={t('welcomeProCta')}
            testId="welcome-choose-professional"
            variant="primary"
          />

          <RoleCard
            key="role-patient"
            navigate={navigate}
            to="/register/patient"
            icon={UserRound}
            title={t('welcomePatientTitle')}
            subtitle={t('welcomePatientSubtitle')}
            bullets={bullets.patient}
            cta={t('welcomePatCta')}
            testId="welcome-choose-patient"
            variant="accent"
          />
        </motion.div>

        <div className="mt-10 text-center text-sm text-muted-foreground">
          {t('welcomeAlreadyRegistered')} {' '}
          <Link to="/login" className="text-primary font-semibold hover:underline underline-offset-4" data-testid="welcome-goto-login">
            {t('loginNow')}
          </Link>
        </div>

        <div className="mt-6 flex items-center justify-center gap-3 text-xs text-muted-foreground flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full glass"><ShieldCheck className="h-3 w-3 text-accent" /> {t('welcomeBadgeGDPR')}</span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full glass"><Sparkles className="h-3 w-3 text-primary" /> {t('welcomeBadgeAI')}</span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full glass"><Globe className="h-3 w-3" /> 14 lingue</span>
        </div>

        <div className="mt-8 pt-6 border-t border-border/40 flex items-center justify-center gap-4 text-xs text-muted-foreground flex-wrap">
          <Link to="/legal/privacy" className="hover:text-foreground hover:underline" data-testid="welcome-legal-privacy">
            Privacy Policy
          </Link>
          <span className="opacity-40">·</span>
          <Link to="/legal/cookie" className="hover:text-foreground hover:underline" data-testid="welcome-legal-cookie">
            Cookie Policy
          </Link>
          <span className="opacity-40">·</span>
          <Link to="/legal/dpa" className="hover:text-foreground hover:underline" data-testid="welcome-legal-dpa">
            DPA
          </Link>
        </div>
      </div>
    </div>
  );
};

export default WelcomePage;
